import { useMemo } from "react";
import type { ImgHTMLAttributes } from "react";
import avatar01 from "@/assets/images/hash-avatars/account_avatar01.svg";
import avatar02 from "@/assets/images/hash-avatars/account_avatar02.svg";
import avatar03 from "@/assets/images/hash-avatars/account_avatar03.svg";
import avatar04 from "@/assets/images/hash-avatars/account_avatar04.svg";
import avatar05 from "@/assets/images/hash-avatars/account_avatar05.svg";
import avatar06 from "@/assets/images/hash-avatars/account_avatar06.svg";
import avatar07 from "@/assets/images/hash-avatars/account_avatar07.svg";
import avatar08 from "@/assets/images/hash-avatars/account_avatar08.svg";
import avatar09 from "@/assets/images/hash-avatars/account_avatar09.svg";
import avatar10 from "@/assets/images/hash-avatars/account_avatar10.svg";
import avatar11 from "@/assets/images/hash-avatars/account_avatar11.svg";
import avatar12 from "@/assets/images/hash-avatars/account_avatar12.svg";
import { cn } from "./utils";

const HASH_AVATARS = [
  avatar01,
  avatar02,
  avatar03,
  avatar04,
  avatar05,
  avatar06,
  avatar07,
  avatar08,
  avatar09,
  avatar10,
  avatar11,
  avatar12,
] as const;

const AVATAR_COUNT = HASH_AVATARS.length;

/**
 * 将地址字符串稳定映射到 [0, length)，供头像数组下标使用。
 * 使用 32 位 FNV-1a，分布均匀、实现轻量。
 */
export function hashAddressToAvatarIndex(address: string, length = AVATAR_COUNT): number {
  if (length < 1) return 0;
  const id = typeof address === "string" ? address.trim().toLowerCase() : "";
  const seed =
    id.length > 0
      ? id
      : "0x0000000000000000000000000000000000000000";

  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % length;
}

export type HashAvatarProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /** 钱包地址（0x…），作为哈希种子；空串时使用占位种子，保证仍有稳定头像 */
  address: string;
};

/**
 * 根据钱包地址哈希从 12 张预设 SVG 中选一张作为头像。
 */
export function HashAvatar({ address, className, alt, ...rest }: HashAvatarProps) {
  const src = useMemo(() => {
    const index = hashAddressToAvatarIndex(address, AVATAR_COUNT);
    return HASH_AVATARS[index];
  }, [address]);

  const resolvedAlt =
    alt ??
    (address.trim()
      ? `Avatar ${address.slice(0, 6)}…${address.slice(-4)}`
      : "User avatar");

  return (
    <img
      src={src}
      alt={resolvedAlt}
      className={cn(
        "h-6 w-6 shrink-0 rounded-full object-cover ring-1 ring-slate-200/80 dark:ring-[#D2F0E1]/10",
        className
      )}
      draggable={false}
      {...rest}
    />
  );
}
